import type {
  Article,
  ArticleCategory,
  GetArticlesParams,
  CreateArticlePayload,
  UpdateArticlePayload,
} from '@shared/types/entities';

import { api } from './client';
import { type PaginatedResponse, buildQuery } from './types';

export type { Article, ArticleCategory, GetArticlesParams, CreateArticlePayload, UpdateArticlePayload };

// ─── Articles ──────────────────────────────────────────

export async function getArticlesApi(params?: GetArticlesParams): Promise<PaginatedResponse<Article>> {
  return api.get(`/blog/articles${buildQuery(params as Record<string, unknown>)}`);
}

/**
 * Projection renvoyée par le listing public (31/07/2026).
 *
 * L'API y omet volontairement deux champs présents sur `Article` :
 * - `content` : le HTML complet n'a rien à faire dans une liste de vignettes.
 *   `readTimeMinutes` est en contrepartie toujours calculé côté serveur.
 * - `author` : l'identité du rédacteur ne sort pas en public (RGPD). Elle reste
 *   disponible sur le détail d'article (`getArticleBySlugApi`).
 *
 * Le type est distinct de `Article` pour que le compilateur signale toute vue
 * publique qui tenterait de lire ces champs, au lieu d'afficher `undefined`.
 */
export type ArticleListItem = Omit<Article, 'content' | 'author'>;

export async function getPublishedArticlesApi(params?: GetArticlesParams): Promise<PaginatedResponse<ArticleListItem>> {
  return api.get(`/blog/articles/published${buildQuery(params as Record<string, unknown>)}`);
}

export async function getArticleApi(id: number): Promise<Article> {
  return api.get(`/blog/articles/${id}`);
}

export async function getArticleBySlugApi(slug: string): Promise<Article> {
  return api.get(`/blog/articles/by-slug/${slug}`);
}

export async function createArticleApi(data: CreateArticlePayload): Promise<Article> {
  return api.post('/blog/articles', data);
}

export async function updateArticleApi(id: number, data: UpdateArticlePayload): Promise<Article> {
  return api.patch(`/blog/articles/${id}`, data);
}

export async function deleteArticleApi(id: number): Promise<void> {
  return api.delete(`/blog/articles/${id}`);
}

// ─── Categories ────────────────────────────────────────

/**
 * Lot A bis (30/07/2026) — les catégories sont isolées par blog.
 *
 * Sans scope : référentiel éditorial BoulesConnect (comportement historique).
 * Avec scope : taxonomie EXCLUSIVE du club ou de l'organisation.
 *
 * En écriture, le serveur ignore délibérément `clubId` — il déduit le scope du
 * JWT. `organizationId` ne sert qu'à désigner la structure cible d'un admin
 * multi-organisations, et reste validé côté serveur.
 */
export type ArticleCategoryScope = {
  clubId?: number;
  organizationId?: number;
};

export async function getArticleCategoriesApi(scope?: ArticleCategoryScope): Promise<ArticleCategory[]> {
  return api.get(`/article-categories${buildQuery(scope as Record<string, unknown>)}`);
}

export async function createArticleCategoryApi(
  data: {
    name: string;
    slug?: string;
    color?: string;
  },
  scope?: ArticleCategoryScope,
): Promise<ArticleCategory> {
  return api.post(`/article-categories${buildQuery(scope as Record<string, unknown>)}`, data);
}

export async function updateArticleCategoryApi(
  id: number,
  data: { name?: string; slug?: string; color?: string },
  scope?: ArticleCategoryScope,
): Promise<ArticleCategory> {
  return api.patch(`/article-categories/${id}${buildQuery(scope as Record<string, unknown>)}`, data);
}

export async function deleteArticleCategoryApi(id: number, scope?: ArticleCategoryScope): Promise<void> {
  return api.delete(`/article-categories/${id}${buildQuery(scope as Record<string, unknown>)}`);
}
